import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Drawer from "@/components/ui/drawer";
import { Document, Download } from "@/components/icons";
import { PollTextarea } from "@/components/ui/poll-textarea";
import { PollRadio } from "@/components/ui/poll-radio";
import { PollCheckbox } from "@/components/ui/poll-checkbox";
import { PollStar } from "@/components/ui/poll-star";
import { PollNumbers } from "@/components/ui/poll-numbers";
import type { PollAttendee } from "../../types/polls.types";
import type {
  SurveyAnswerDetailResDto,
  SurveyAttemptDetailResDto,
} from "../../types/polls.types";
import { pollsApi } from "../../services/polls.api";
import styles from "./AttendeeAnswersDrawer.module.scss";
import previewStyles from "../PollPreviewStep/PollPreviewStep.module.scss";

interface AttendeeAnswersDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  attendee: PollAttendee | null;
  pollId?: string;
  pollName?: string;
}

function parseNumericOption(text: string | undefined): number | null {
  if (!text || typeof text !== "string") return null;
  const trimmed = text.trim();
  const num = parseInt(trimmed, 10);
  if (Number.isNaN(num)) return null;
  return num;
}

const AttendeeAnswersDrawer: React.FC<AttendeeAnswersDrawerProps> = ({
  isOpen,
  onClose,
  attendee,
  pollName,
}) => {
  const { t } = useTranslation();
  const [data, setData] = useState<SurveyAttemptDetailResDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !attendee?.id) {
      setData(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    pollsApi
      .getAttemptAnswers(attendee.id)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : "Failed to load answers",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, attendee?.id]);

  const renderAnswer = (answer: SurveyAnswerDetailResDto, index: number) => {
    const hasText =
      answer.textValue != null && String(answer.textValue).trim() !== "";
    const hasMulti =
      answer.selectedOptions && answer.selectedOptions.length > 0;
    const hasSingle = answer.selectedOption?.optionText != null;
    const questionNum = index + 1;
    const questionText =
      answer.questionText ||
      t("createPoll.preview.untitledQuestion");

    const questionLabel = (
      <p className={previewStyles.questionText}>
        {questionNum}. {questionText}
      </p>
    );

    if (hasText) {
      return (
        <div key={answer.id} className={previewStyles.questionItem}>
          {questionLabel}
          <PollTextarea
            value={answer.textValue ?? ""}
            readOnly
            disabled
            rows={4}
          />
        </div>
      );
    }

    if (hasMulti) {
      return (
        <div key={answer.id} className={previewStyles.questionItem}>
          <p className={previewStyles.questionText}>
            {questionNum}. {questionText}
            <span className={previewStyles.questionHint}>
              {" "}
              ({t("createPoll.preview.multipleChoiceHint")})
            </span>
          </p>
          <div className={previewStyles.optionsList}>
            {answer.selectedOptions!.map((opt) => (
              <PollCheckbox
                key={opt.id}
                label={opt.optionText}
                checked
                disabled
                onChange={() => {}}
              />
            ))}
          </div>
        </div>
      );
    }

    if (hasSingle) {
      const optText = answer.selectedOption!.optionText;
      const num = parseNumericOption(optText);
      const isStarRange = num !== null && num >= 1 && num <= 5;
      const isScaleRange =
        num !== null && num >= 1 && num <= 10 && !isStarRange;

      if (isStarRange) {
        return (
          <div key={answer.id} className={previewStyles.questionItem}>
            {questionLabel}
            <PollStar
              value={num!}
              maxStars={5}
              size={32}
              disabled
              onChange={() => {}}
            />
          </div>
        );
      }

      if (isScaleRange) {
        const max = num! <= 5 ? 5 : 10;
        return (
          <div key={answer.id} className={previewStyles.questionItem}>
            {questionLabel}
            <PollNumbers
              value={num!}
              min={1}
              max={max}
              disabled
              onChange={() => {}}
              labelMin={t("createPoll.preview.scaleMin")}
              labelMax={t("createPoll.preview.scaleMax")}
            />
          </div>
        );
      }

      return (
        <div key={answer.id} className={previewStyles.questionItem}>
          {questionLabel}
          <div className={previewStyles.optionsList}>
            <PollRadio
              name={`answer-${answer.id}`}
              label={optText}
              value={answer.selectedOption!.id}
              checked
              disabled
              onChange={() => {}}
            />
          </div>
        </div>
      );
    }

    return (
      <div key={answer.id} className={previewStyles.questionItem}>
        {questionLabel}
        <PollTextarea
          value={t("polls.attendees.noAnswer")}
          readOnly
          disabled
          rows={1}
        />
      </div>
    );
  };

  const handleDownload = () => {
    if (!data || !attendee) return;
    // Export single attendee answers as CSV (same format as full export)
    const rows: string[][] = [
      [
        t("polls.attendees.exportQuestion"),
        t("polls.attendees.exportAnswer"),
      ],
    ];
    data.answers?.forEach((a) => {
      const answerText =
        a.textValue ??
        a.selectedOption?.optionText ??
        a.selectedOptions?.map((o) => o.optionText).join(", ") ??
        "";
      rows.push([a.questionText ?? "", answerText]);
    });
    const BOM = "\uFEFF";
    const csv = BOM + rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";")).join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `anket-cevaplari-${attendee.name ?? attendee.id}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      headerIcon={<Document width={24} height={24} color="#14181F" />}
      title={t("polls.attendees.answersTitle")}
      titleBadge={attendee ? <span>{attendee.name}</span> : undefined}
      headerActions={
        data?.answers?.length ? (
          <button
            type="button"
            className="drawer__header-action"
            onClick={handleDownload}
            aria-label={t("common.download")}
          >
            <Download width={20} height={20} color="#34314A" />
          </button>
        ) : undefined
      }
      position="right"
      width="560px"
    >
      <div className={styles.drawerContent}>
        {loading && (
          <div className={styles.loading}>
            {t("common.loading")}
          </div>
        )}
        {error && (
          <div className={styles.error}>
            <span>{error}</span>
          </div>
        )}
        {!loading && !error && data && (
          <>
            {!data.answers || data.answers.length === 0 ? (
              <div className={styles.emptyAnswers}>
                {t("polls.attendees.noAnswers")}
              </div>
            ) : (
              <div className={styles.answersSection}>
                <h4 className={styles.pollName}>
                  {pollName ?? t("polls.attendees.defaultPollName")}
                </h4>
                <div className={styles.answersWrapper}>
                  <div className={previewStyles.questionsList}>
                    {data.answers.map((answer, index) =>
                      renderAnswer(answer, index),
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Drawer>
  );
};

export default AttendeeAnswersDrawer;
